import { useCallback, useMemo, useRef, useState } from 'react';
import { parseAmount } from '../utils/amountParser';
import { tradeSide } from '../utils/subAccounts';

// A number field (units, a price) as a number: its inputs are `type="number"`,
// so a typed value is '' or a plain decimal ('0.08046'), which the amount
// parser would read as thousands when it has three decimals.
const toNumber = (value) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * The units and price a draft holds: what the user typed (`trade_quantity`,
 * `trade_unit_price`) or, untouched, the saved trade's.
 */
export function tradeFields(draft) {
  return {
    quantity: 'trade_quantity' in (draft || {}) ? toNumber(draft.trade_quantity) : toNumber(draft?.trade?.quantity),
    unitPrice: 'trade_unit_price' in (draft || {}) ? toNumber(draft.trade_unit_price) : toNumber(draft?.trade?.unit_price),
  };
}

/**
 * A money field's value as a number. A stored amount is already a number; a
 * typed one is the text the user typed, read by the separator-aware parser
 * (`66.000` is 66000, `12,50` is 12.5). Null when there is nothing to read.
 */
export function toAmount(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  return parseAmount(value);
}

/**
 * The exchange rule of a transfer: the "Amount received" field shows, and
 * `currency_exchange` is sent, once both legs are picked (or an exchange is
 * already there) and the two accounts do not share exactly one identical
 * currency.
 */
export function exchangeRule(draft, accounts = []) {
  const fromAccount = accounts.find(a => a.id === draft?.from_account_id);
  const toAccount = accounts.find(a => a.id === draft?.to_account_id);
  const accountsShareSingleCurrency =
    fromAccount?.currencies?.length === 1 &&
    toAccount?.currencies?.length === 1 &&
    fromAccount.currencies[0] === toAccount.currencies[0];
  // Before both accounts are picked there is nothing to exchange between, so
  // the row stays hidden, unless an exchange already exists.
  const bothTransferAccountsSelected = Boolean(draft?.from_account_id && draft?.to_account_id);
  const showExchangeRow =
    !accountsShareSingleCurrency &&
    (bothTransferAccountsSelected || Boolean(draft?.currency_exchange));
  return { accountsShareSingleCurrency, showExchangeRow };
}

/** What `POST /movements` and `PUT /movements/{id}` get for a draft. */
export function buildPayload(draft, accounts = []) {
  const payload = {
    type: draft.type,
    date: draft.date,
    amount: toAmount(draft.amount),
    note: draft.note || '',
    description: draft.description || '',
    currency: draft.currency,
  };

  if (draft.type === 'Transfer') {
    payload.from_account_id = draft.from_account_id;
    payload.to_account_id = draft.to_account_id;
    const { accountsShareSingleCurrency } = exchangeRule(draft, accounts);
    if (
      !accountsShareSingleCurrency &&
      draft.currency_exchange?.value_target &&
      draft.currency_exchange?.currency_target
    ) {
      payload.currency_exchange = {
        currency_target: draft.currency_exchange.currency_target,
        value_target: toAmount(draft.currency_exchange.value_target),
      };
    }
  } else {
    payload.account_id = draft.account_id;
    payload.category_id = draft.category_id;
  }

  // The units and price of a buy, sell or receive. Sent only once typed, so a
  // save that never touched them leaves the trade as it is; units cleared on
  // a movement that had a trade remove it. No price means the server works it
  // out from the amount.
  if ('trade_quantity' in draft || 'trade_unit_price' in draft) {
    const { quantity, unitPrice } = tradeFields(draft);
    if (quantity > 0 && tradeSide(draft, accounts)) {
      payload.trade = { quantity, unit_price: unitPrice > 0 ? unitPrice : null };
    } else if (draft.trade) {
      payload.trade = null;
    }
  }

  return payload;
}

/**
 * The fields a type change clears: the legs or the account that the new type
 * does not use, and the category, whose list depends on the type.
 */
function clearedByTypeChange(fromType, toType) {
  if (toType === 'Transfer') return { category_id: null, account_id: null };
  if (fromType === 'Transfer') return { from_account_id: null, to_account_id: null };
  return { category_id: null };
}

/**
 * Merge a `POST /movements/parse` result into a draft: every non-null key is
 * written, except to a field the user has touched. A null key never blanks a
 * default. When the parse changes the type, what that change would clear is
 * cleared, unless the parse filled it.
 *
 * `carryAccount` moves the account across a parsed type change the way quick
 * add's type segments do: a purchase's account becomes the transfer's From
 * leg, and back, when the parse names no account for it.
 *
 * Returns `{ draft, filled }`, `filled` being the keys it wrote.
 */
export function mergeParsed(draft, parsed, touched = new Set(), { carryAccount = false } = {}) {
  const filled = [];
  const next = { ...draft };
  Object.entries(parsed || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || key === 'tag_movements') return;
    if (touched.has(key)) return;
    next[key] = value;
    filled.push(key);
  });
  if (filled.includes('type') && next.type !== draft.type) {
    Object.entries(clearedByTypeChange(draft.type, next.type)).forEach(([key, value]) => {
      if (!filled.includes(key)) next[key] = value;
    });
    if (carryAccount) {
      const toTransfer = next.type === 'Transfer';
      const fromTransfer = draft.type === 'Transfer';
      const carried = toTransfer ? draft.account_id : fromTransfer ? draft.from_account_id : null;
      const leg = toTransfer ? 'from_account_id' : 'account_id';
      if ((toTransfer || fromTransfer) && carried != null && next[leg] == null) next[leg] = carried;
    }
  }
  next.tag_movements = draft.tag_movements || [];
  return { draft: next, filled };
}

/**
 * One movement being written or edited, shared by the desktop detail panel
 * and the phone's quick add.
 *
 * `draft` is the movement as edited. Money fields keep what the user typed
 * (the amount, and the exchange's `value_target`); `buildMovementPayload`
 * reads them with the amount parser. `touched` is the set of fields the user
 * changed by hand since the draft was last reset: a parse or a remembered
 * "as last time" value never overwrites one of those.
 *
 * @param {{ accounts?: object[] }} options - the accounts the exchange rule reads
 */
export default function useMovementDraft({ accounts = [] } = {}) {
  const [draft, setDraft] = useState(null);
  const touchedRef = useRef(new Set());

  /**
   * Start over from `movement`. Nothing is touched, except the fields in
   * `keepTouched` that already were (quick add's "Save and add another" keeps
   * the account and date the user chose).
   */
  const resetDraft = useCallback((movement, keepTouched = []) => {
    touchedRef.current = new Set(keepTouched.filter(field => touchedRef.current.has(field)));
    setDraft(movement);
  }, []);

  const isTouched = useCallback((field) => touchedRef.current.has(field), []);

  /**
   * Set a field. `touch: false` sets it as a default would (quick add's last
   * account, the currency that follows it), so a later fill may still replace
   * it. Setting the type clears what it does not use, the category included,
   * even when it is the type already set (the desktop panel always has).
   */
  const handleFieldChange = useCallback((field, value, { touch = true } = {}) => {
    if (touch) touchedRef.current.add(field);
    setDraft(prev => {
      if (field === 'type' && prev) {
        return { ...prev, type: value, ...clearedByTypeChange(prev.type, value) };
      }
      return { ...prev, [field]: value };
    });
  }, []);

  /**
   * Fill the untouched fields from `values` (a parse result, or a remembered
   * movement). Returns the keys it wrote, so the caller can mark them.
   *
   * `respectTouched: false` writes over typed fields too: the desktop AI box,
   * where submitting a description is the request to replace the form.
   * `carryAccount` is `mergeParsed`'s.
   */
  const fillUntouched = useCallback((values, { respectTouched = true, carryAccount = false } = {}) => {
    const touched = respectTouched ? new Set(touchedRef.current) : new Set();
    const filled = Object.keys(values || {}).filter(key => (
      values[key] !== null && values[key] !== undefined && key !== 'tag_movements' && !touched.has(key)
    ));
    setDraft(prev => mergeParsed(prev, values, touched, { carryAccount }).draft);
    return filled;
  }, []);

  const { accountsShareSingleCurrency, showExchangeRow } = useMemo(
    () => exchangeRule(draft, accounts),
    [draft, accounts],
  );

  const buildMovementPayload = useCallback(() => buildPayload(draft, accounts), [draft, accounts]);

  return {
    draft,
    setDraft,
    resetDraft,
    isTouched,
    handleFieldChange,
    mergeParsed: fillUntouched,
    buildMovementPayload,
    accountsShareSingleCurrency,
    showExchangeRow,
    amountValue: draft ? toAmount(draft.amount) : null,
  };
}
